/**
 * Helper unificado para resolver imágenes individuales y colecciones/carrusel de imágenes de artículos.
 * Soporta múltiples imágenes separadas por coma, punto y coma, barra vertical o arrays.
 */

const getBackendHost = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://127.0.0.1:8000';
    }
  }
  return '';
};

const getActiveCompanyFolder = () => {
  if (typeof window === 'undefined') return '';
  try {
    const rawUser = sessionStorage.getItem('tabUserData') || localStorage.getItem('userData');
    const parsed = rawUser ? JSON.parse(rawUser) : null;
    const emp = parsed?.empresa_activa;
    const dbName = String(emp?.db_name || emp?.d_basdat || '').trim().toLowerCase();
    if (dbName) {
      if (dbName.startsWith('sigecom_emp')) {
        return `hiperpel${dbName.replace('sigecom_emp', '')}`;
      }
      return dbName;
    }
    const compId = String(
      emp?.c_nuges ||
      emp?.id ||
      window.__ACTIVE_COMPANY_ID__ ||
      sessionStorage.getItem('tabCompanyId') ||
      localStorage.getItem('lastCompanyId') ||
      ''
    ).trim();
    if (compId) return `hiperpel${compId}`;
  } catch {}
  return '';
};

/**
 * Extrae la lista de nombres de archivo o URLs de imágenes de un artículo.
 * @param {Object} article 
 * @returns {string[]} Lista de strings individuales
 */
export const extractArticleImageNames = (article) => {
  if (!article) return [];

  // Si ya viene formateado como array
  if (Array.isArray(article.imagenes) && article.imagenes.length > 0) {
    return article.imagenes.filter(Boolean);
  }
  if (Array.isArray(article.images) && article.images.length > 0) {
    return article.images.filter(Boolean);
  }

  const rawImgField = article.d_imagen || article.imagen || article.foto || '';
  if (!rawImgField || typeof rawImgField !== 'string' || !rawImgField.trim()) {
    return [];
  }

  // Dividir por comas, punto y coma o barras verticales
  return rawImgField
    .split(/[,;|]/)
    .map(s => s.trim())
    .filter(s => s && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined' && s.toLowerCase() !== 'none');
};

/**
 * Genera la lista ordenada de URLs candidatas para una imagen específica.
 * @param {string} rawImgName 
 * @param {string} dPathImg 
 * @param {string} baseImageUrl 
 * @returns {string[]}
 */
export const getCandidateUrlsForImage = (rawImgName, dPathImg = '', baseImageUrl = '') => {
  if (!rawImgName || !rawImgName.trim()) return [];

  const urls = [];
  const backendHost = getBackendHost();

  let imgStr = rawImgName.trim().replace(/\\/g, '/');
  let pathImgStr = (dPathImg || '').trim().replace(/\\/g, '/');

  // Si imgStr o pathImgStr contienen rutas absolutas locales de Windows (ej: C:/..., F:/...),
  // provenientes de FoxPro, ignorar la unidad de disco local y usar solo el nombre del archivo
  if (/^[a-zA-Z]:/.test(imgStr)) {
    const parts = imgStr.split('/');
    imgStr = parts[parts.length - 1];
  }
  if (/^[a-zA-Z]:/.test(pathImgStr)) {
    pathImgStr = '';
  }

  if (pathImgStr) {
    if (pathImgStr.startsWith('/')) pathImgStr = pathImgStr.substring(1);
    if (!pathImgStr.endsWith('/')) pathImgStr += '/';
  }

  // Si ya es URL absoluta
  if (imgStr.startsWith('http://') || imgStr.startsWith('https://') || imgStr.startsWith('data:')) {
    return [imgStr];
  }
  if (imgStr.startsWith('www.')) {
    return [`https://${imgStr}`, `http://${imgStr}`];
  }
  if (imgStr.startsWith('//')) {
    return [`https:${imgStr}`];
  }

    const cleanImgStr = imgStr.startsWith('/') ? imgStr.substring(1) : imgStr;
  const activeFolder = getActiveCompanyFolder();

  // Lista ordenada de subcarpetas prioritarias:
  // 1. Nueva estructura unificada: <empresa>/articulos/ (ej: hiperpel3/articulos/)
  // 2. Ruta provista por la DB (d_pathimg)
  // 3. Fallback histórico: articulos/<empresa>/ (ej: articulos/hiperpel3/)
  const subpathCandidates = [];
  if (activeFolder) {
    subpathCandidates.push(`${activeFolder}/articulos/`);
  }
  if (pathImgStr) {
    subpathCandidates.push(pathImgStr);
    // Si viene articulos/hiperpel3, probar también hiperpel3/articulos
    if (pathImgStr.startsWith('articulos/')) {
      const rest = pathImgStr.replace('articulos/', '');
      subpathCandidates.push(`${rest}articulos/`);
    }
  }
  if (activeFolder) {
    subpathCandidates.push(`articulos/${activeFolder}/`);
  }
  if (subpathCandidates.length === 0) {
    subpathCandidates.push('');
  }

  // 1. URL base provista explícitamente (si existe)
  if (baseImageUrl) {
    let base = baseImageUrl.trim();
    if (!base.endsWith('/')) base += '/';
    urls.push(`${base}${cleanImgStr}`);
  }

  // 2 y 3. Generar candidatos tanto para backendHost (dev) como rutas relativas web
  for (const sub of subpathCandidates) {
    if (backendHost) {
      urls.push(`${backendHost}/media/${sub}${cleanImgStr}`);
    }
    urls.push(`/media/${sub}${cleanImgStr}`);
  }

  return Array.from(new Set(urls.filter(Boolean)));
};

/**
 * Obtiene todas las imágenes de un artículo, cada una con su lista de URLs candidatas.
 * @param {Object} article 
 * @param {string} baseImageUrl 
 * @returns {Array<{ index: number, raw: string, candidates: string[], primaryUrl: string }>}
 */
export const getArticleImageSlides = (article, baseImageUrl = '') => {
  const imageNames = extractArticleImageNames(article);
  if (imageNames.length === 0) return [];

  const pathImg = article.d_pathimg || '';
  return imageNames.map((rawName, index) => {
    const candidates = getCandidateUrlsForImage(rawName, pathImg, baseImageUrl);
    return {
      index,
      raw: rawName,
      candidates,
      primaryUrl: candidates[0] || ''
    };
  });
};

/**
 * Obtiene la URL principal para una tarjeta de producto (la primera imagen disponible).
 * @param {Object} article 
 * @returns {string|null}
 */
export const getProductFirstImageUrl = (article) => {
  const slides = getArticleImageSlides(article);
  if (slides.length > 0 && slides[0].candidates.length > 0) {
    return slides[0].candidates[0];
  }
  return null;
};

/**
 * Obtiene la lista ordenada de URLs candidatas para un asset general (logotipo, banner, icono, etc.).
 * Permite que el frontend intente cargarlo desde la ruta local o desde el servidor Django en dev.
 * @param {string} rawUrl 
 * @returns {string[]}
 */
export const getImageCandidateUrls = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== 'string' || !rawUrl.trim()) return [];
  const trimmed = rawUrl.trim().replace(/\\/g, '/');
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return [trimmed];
  }

  const backendHost = getBackendHost();
  const cleanPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const candidates = [];

  // 1. Ruta tal cual (relativa al frontend)
  candidates.push(cleanPath);

  // 2. Ruta con backendHost
  if (backendHost) {
    candidates.push(`${backendHost}${cleanPath}`);
    if (cleanPath.startsWith('/images/landing/')) {
      const sub = cleanPath.replace('/images/landing/', '');
      candidates.push(`${backendHost}/media/landing/${sub}`);
      candidates.push(`${backendHost}/images/landing/${sub}`);
    } else if (cleanPath.startsWith('/media/landing/')) {
      const sub = cleanPath.replace('/media/landing/', '');
      candidates.push(`${backendHost}/media/landing/${sub}`);
      candidates.push(`${backendHost}/images/landing/${sub}`);
    }
  }

  // 3. Variaciones entre /images/ y /media/
  if (cleanPath.startsWith('/images/landing/')) {
    candidates.push(cleanPath.replace('/images/landing/', '/media/landing/'));
  } else if (cleanPath.startsWith('/media/landing/')) {
    candidates.push(cleanPath.replace('/media/landing/', '/images/landing/'));
  }

  return Array.from(new Set(candidates.filter(Boolean)));
};

/**
 * Resuelve una URL simple de imagen garantizando acceso en dev/prod.
 * @param {string} rawUrl 
 * @returns {string}
 */
export const resolveImageUrl = (rawUrl) => {
  const list = getImageCandidateUrls(rawUrl);
  return list[0] || rawUrl || '';
};
